import { getLocalTimeZone, today } from '@internationalized/date';
import { useControlledState } from '@react-stately/utils';
import { Calendar as CalendarIcon } from '@untitledui/icons';
import { useCallback, useMemo, useRef } from 'react';
import { useDateFormatter } from 'react-aria';
import type {
  DatePickerProps as AriaDatePickerProps,
  DateValue,
  TimeValue,
} from 'react-aria-components';
import {
  Button as AriaButton,
  DatePicker as AriaDatePicker,
  Dialog as AriaDialog,
  Group as AriaGroup,
  Popover as AriaPopover,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { Button } from '@/lib/base/buttons/button';
import {
  SELECT_TRIGGER_BASE,
  SELECT_TRIGGER_FOCUS,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select';
import { useDismissOnInteractOutside } from '@/lib/base/overlays/useDismissOnInteractOutside';
import { TimeField } from '@/lib/base/pickers/time-picker/time-field';
import { cx } from '@/utils/cx.ts';

import { Calendar } from './calendar.tsx';

type DatePickerProps = AriaDatePickerProps<DateValue> & {
  /**
   * Trigger size, on the shared field-control scale — the same prop and the
   * same values as Input, Select and the rest of `lib/base/forms`, so a date
   * picker lines up with the fields beside it. Defaults to `'sm'` (40px).
   *
   * Replaces the former `compact` boolean, which forced its geometry with
   * `!important` overrides against the Button defaults; `size='xs'` is the
   * equivalent and no longer needs to fight anything.
   */
  size?: SelectSize;
  /**
   * Renders the trigger without its own border/background, for dense inline
   * surfaces (table cells, the note composer bar) where the field chrome would
   * be visual noise. Independent of `size`.
   */
  borderless?: boolean;
  /**
   * Placeholder shown when no date is selected. Defaults to "Select date".
   */
  placeholder?: string;
  /**
   * When true, renders a "Clear date" button at the bottom of the calendar
   * popover so the user can remove the selected date. Clearing also closes
   * the popover.
   */
  clearable?: boolean;
  /**
   * Opt-in: render a clearable time field inside the popover (below the
   * calendar) once a date is selected. The time is controlled separately via
   * `timeValue` / `onTimeChange` — this component only manages the date value;
   * the caller decides how to combine date + time (e.g. store a datetime when
   * a time is set, a plain date otherwise). Clearing the date also clears the
   * time. Off by default so existing date pickers are unaffected.
   */
  showTime?: boolean;
  /** Controlled time value (used when `showTime`). `null` = no time set. */
  timeValue?: TimeValue | null;
  /** Called when the time changes (or is cleared, with `null`). */
  onTimeChange?: (value: TimeValue | null) => void;
  /**
   * Hour cycle for the trigger's time label and the popover's TimeField
   * (used when `showTime`). Defaults to 12-hour to preserve existing
   * behavior for external/booking callers; in-app callers should pass the
   * org's configured format via `useOrgTimeFormat()`.
   */
  hourCycle?: 12 | 24;
};

export const DatePicker = ({
  value: valueProp,
  defaultValue,
  onChange,
  isOpen: isOpenProp,
  defaultOpen,
  onOpenChange,
  className,
  size = 'sm',
  borderless = false,
  placeholder = 'Select date',
  clearable = false,
  showTime = false,
  timeValue = null,
  onTimeChange,
  hourCycle = 12,
  ...props
}: DatePickerProps) => {
  // The smallest trigger is used for dense inline surfaces (table cells), where
  // the roomy default popover would tower over the row it belongs to.
  const isDensePopover = size === 'xs';
  const highlightedDates = useMemo(() => [today(getLocalTimeZone())], []);
  const formatter = useDateFormatter({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeFormatter = useDateFormatter({
    hour: 'numeric',
    minute: '2-digit',
    // Match the popover's TimeField's hour cycle — with the locale default,
    // a mismatched cycle renders "17:00" on the trigger while the field
    // below shows "5:00 pm" (or vice versa).
    hour12: hourCycle === 12,
  });
  const zIndex = useStackingZIndex();
  const [value, setValue] = useControlledState(
    valueProp,
    defaultValue || null,
    onChange
  );
  // Honour caller-provided `isOpen` / `onOpenChange` when supplied; fall back
  // to internal state so the `clearable` action below can close the popover
  // on its own without the caller wiring up open state.
  const [isOpen, setIsOpen] = useControlledState(
    isOpenProp,
    defaultOpen ?? false,
    onOpenChange
  );

  // The popover is non-modal (see the AriaPopover below), which disables
  // react-aria's built-in outside-pointer dismissal; restore it here (full
  // rationale in useDismissOnInteractOutside).
  const popoverRef = useRef<HTMLElement>(null);
  const handleDismissOutside = useCallback(() => setIsOpen(false), [setIsOpen]);
  useDismissOnInteractOutside(popoverRef, isOpen, handleDismissOutside);

  const formattedDate = value
    ? formatter.format(value.toDate(getLocalTimeZone()))
    : placeholder;

  // When a time is set (showTime mode), surface it beside the date on the
  // trigger so a timed entry reads e.g. "5 Jun 2026 · 11:00 AM" rather than
  // looking identical to a date-only one.
  const triggerLabel =
    showTime && value && timeValue != null
      ? `${formattedDate} · ${timeFormatter.format(
          new Date(2000, 0, 1, timeValue.hour, timeValue.minute)
        )}`
      : formattedDate;

  const handleClear = useCallback(() => {
    // A time without a date is meaningless — clear both. Time FIRST: a
    // controlled caller's onTimeChange typically re-derives its combined
    // value from the (still-set) date, so clearing the time after the date
    // would re-emit the old date and resurrect the value being cleared.
    onTimeChange?.(null);
    setValue(null);
    setIsOpen(false);
  }, [setValue, onTimeChange, setIsOpen]);

  const handleClearTime = useCallback(() => {
    onTimeChange?.(null);
  }, [onTimeChange]);

  const handleDone = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <AriaDatePicker
      {...props}
      value={value}
      onChange={setValue}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      // In time mode, selecting a date must NOT close the popover — the user
      // still needs to set the time. They confirm with the "Done" button.
      // Date-only pickers keep the default close-on-select behavior.
      shouldCloseOnSelect={!showTime}
    >
      <AriaGroup className={className as string | undefined}>
        {/* Field-styled trigger: same base classes and size scale as Select,
            so a date picker and a select at the same `size` are the same
            height and read as the same kind of control. */}
        {/* Focus ring while OPEN as well as on keyboard focus — the same rule
            as Select. Focus sits in the portaled calendar while the picker is
            open, so a focus-visible-only ring would leave the trigger looking
            idle exactly when the user is working in it. */}
        <AriaButton
          slot='trigger'
          isDisabled={props.isDisabled}
          className={({ isFocusVisible }) =>
            cx(
              SELECT_TRIGGER_BASE,
              'gap-2 text-left text-primary',
              sizes[size].root,
              (isOpen || isFocusVisible) && SELECT_TRIGGER_FOCUS,
              'disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled',
              borderless && 'bg-transparent shadow-none ring-0'
            )
          }
        >
          <CalendarIcon
            aria-hidden='true'
            className={cx('shrink-0 text-fg-quaternary', sizes[size].icon)}
          />
          {/* `data-text` mirrors the label slot the lib/base Button emitted
              when this trigger was a Button — callers target it to hide the
              label and render an icon-only trigger (see TaskFormMetadataBar's
              bar). Removing it silently breaks them. */}
          <span
            data-text
            className={cx('flex-1 truncate', !value && 'text-placeholder')}
          >
            {triggerLabel}
          </span>
        </AriaButton>
      </AriaGroup>
      <AriaPopover
        ref={popoverRef}
        offset={8}
        // Start-aligned like every other picker's menu (AssociatedCrmObjectInput,
        // Dropdown): the calendar is narrower than a full-width trigger, and
        // plain 'bottom' centred it under the field, out of line with the
        // value it edits.
        placement='bottom start'
        shouldFlip
        containerPadding={16}
        // Non-modal — see DropdownPopover (overlays/dropdown.tsx) for the full
        // rationale: the modal underlay renders at z-index auto and paints
        // beneath stacked surfaces (slideouts), so outside-click dismissal
        // never fires there and the calendar gets stuck open. Outside-pointer
        // dismissal is restored by useDismissOnInteractOutside above.
        isNonModal
        className={({ isEntering, isExiting }) =>
          cx(
            // Visual container + scroll live HERE (not on the inner Dialog) so
            // they respect react-aria's computed `maxHeight` (available space
            // after `shouldFlip`/`containerPadding`). A fixed max-height on the
            // Dialog would override that and let tall content (calendar + time
            // + footer) overflow the viewport — the bottom-cutoff bug.
            'origin-(--trigger-anchor-point) will-change-transform',
            'flex flex-col overflow-y-auto rounded-2xl bg-primary shadow-xl ring ring-secondary_alt',
            isEntering &&
              'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
            isExiting &&
              'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5'
          )
        }
        style={{ zIndex }}
      >
        <StackingLayer>
          <AriaDialog className='outline-hidden'>
            {/* In time mode the popover also carries a Time field + footer, so
                the calendar is shown compact (no redundant DD/MM/YYYY row,
                tighter rhythm) and padding is reduced — this keeps the whole
                popover short enough to render fully on small screens instead
                of scrolling. An `xs` trigger (inline table-cell editing) applies
                the same tighter rhythm and padding so the popover matches the
                table's scale; it keeps the DD/MM/YYYY row (unlike time mode). */}
            <div
              className={cx(
                'flex',
                showTime || isDensePopover ? 'px-4 py-3' : 'px-6 py-5'
              )}
            >
              <Calendar
                highlightedDates={highlightedDates}
                hideDateInput={showTime}
                compact={showTime || isDensePopover}
              />
            </div>
            {showTime && value && (
              <div className='flex items-center gap-2 border-t border-secondary px-4 py-2.5'>
                <span className='text-sm font-medium text-secondary'>Time</span>
                <TimeField
                  value={timeValue}
                  onChange={onTimeChange}
                  hourCycle={hourCycle}
                  aria-label='Time'
                  className='flex-1'
                />
                {timeValue != null && (
                  <Button size='sm' color='tertiary' onClick={handleClearTime}>
                    Clear
                  </Button>
                )}
              </div>
            )}
            {value && (clearable || showTime) && (
              <div className='flex items-center justify-between gap-2 border-t border-secondary px-4 py-2.5'>
                {clearable ? (
                  <Button size='sm' color='tertiary' onClick={handleClear}>
                    Clear date
                  </Button>
                ) : (
                  <span />
                )}
                {showTime && (
                  <Button size='sm' color='secondary' onClick={handleDone}>
                    Done
                  </Button>
                )}
              </div>
            )}
          </AriaDialog>
        </StackingLayer>
      </AriaPopover>
    </AriaDatePicker>
  );
};
